import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, message, Upload, Space, Popconfirm, Select, InputNumber } from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { listBanks, createBank, deleteBank, listQuestions, searchQuestions, deleteQuestion, listBankGroups, createBankGroup, updateBankGroup, deleteBankGroup, listExams, createExam, publishExam, updateExam, deleteExam, startAttempt, getExamQuestions, submitAttempt, practice, submitPracticeSession, getMyPracticeRecords, getPracticeSession, deletePracticeSession, importBanksExcel, uploadQuestionsExcel, getExamStats, getExamWrongQuestions, getAttempt, getMyExams, deleteAttempt } from '../api/exams';

// 简单权限判定（后续可从全局上下文取用户权限）
function usePermissions() {
  // 优先从 AuthContext 保存的 user 对象拿权限
  let userObj = null;
  try { userObj = JSON.parse(localStorage.getItem('user')); } catch { userObj = null; }
  const perms = Array.isArray(userObj?.permissions) ? userObj.permissions : [];
  const username = (userObj?.username || userObj?.name || '').toLowerCase();
  // admin 兜底：即使 permissions 里缺少 exam_* 也授予全部考试权限
  const isAdmin = username === 'admin';
  return {
    canManage: isAdmin || perms.includes('exam_manage'),
    canAttempt: isAdmin || perms.includes('exam_attempt'),
    canView: isAdmin || perms.includes('exam_view') || perms.includes('exam_manage'),
    canStats: isAdmin || perms.includes('exam_view') || perms.includes('exam_manage'),
    canMyExams: isAdmin || perms.includes('exam_attempt') || perms.includes('exam_view') || perms.includes('exam_manage')
  };
}

const ExamSystemPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canManage, canView, canAttempt, canStats, canMyExams } = usePermissions();
  const detectMobileView = () => {
    if (typeof window === 'undefined') return false;
    const forceMobileByQuery = new URLSearchParams(window.location.search || '').get('mobileExam') === '1';
    const smallViewport = window.matchMedia('(max-width: 992px)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
    return forceMobileByQuery || smallViewport || coarsePointer;
  };
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionDeleteLoadingId, setQuestionDeleteLoadingId] = useState(null);
  // 组合管理
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupForm] = Form.useForm();
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupDeletingId, setGroupDeletingId] = useState(null);
  // 考试创建
  const [examModalOpen, setExamModalOpen] = useState(false);
  const [exams, setExams] = useState([]);
  const [examsLoaded, setExamsLoaded] = useState(false);
  const [examForm] = Form.useForm();
  const [loadingExams, setLoadingExams] = useState(false);
  const [creatingExam, setCreatingExam] = useState(false);
  const [publishingId, setPublishingId] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishTargetExam, setPublishTargetExam] = useState(null);
  const [publishType, setPublishType] = useState('formal');
  const [editingExam, setEditingExam] = useState(null);
  const [examDeleteLoadingId, setExamDeleteLoadingId] = useState(null);
  // 考试作答相关状态
  const [activeExam, setActiveExam] = useState(null);
  const [examQuestions, setExamQuestions] = useState([]);
  const [attemptId, setAttemptId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [examLoading, setExamLoading] = useState(false);
  const [examSubmitting, setExamSubmitting] = useState(false);
  const [examResult, setExamResult] = useState(null);
  const [examTimeLeft, setExamTimeLeft] = useState(null); // 秒倒计时
  // 练习模式状态
  const [practiceBanks, setPracticeBanks] = useState([]);
  const [practiceInnerTab, setPracticeInnerTab] = useState('select');
  const [practiceSessionId, setPracticeSessionId] = useState(null);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [practiceAnswers, setPracticeAnswers] = useState({});
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceSubmitting, setPracticeSubmitting] = useState(false);
  const [practiceShowResult, setPracticeShowResult] = useState(false);
  const [practiceSummary, setPracticeSummary] = useState(null);
  const [practiceCurrentQuestionIndex, setPracticeCurrentQuestionIndex] = useState(0);
  const [practiceRecordsLoading, setPracticeRecordsLoading] = useState(false);
  const [practiceRecordsSummary, setPracticeRecordsSummary] = useState(null);
  const [practiceRecords, setPracticeRecords] = useState([]);
  const [deletePracticeSessionId, setDeletePracticeSessionId] = useState(null);
  const [practiceRecordModalOpen, setPracticeRecordModalOpen] = useState(false);
  const [practiceRecordLoading, setPracticeRecordLoading] = useState(false);
  const [practiceRecordSession, setPracticeRecordSession] = useState(null);
  const [practiceRecordRows, setPracticeRecordRows] = useState([]);
  const [practiceRecordIndex, setPracticeRecordIndex] = useState(0);
  const [uploadVisible, setUploadVisible] = useState(false); // 题目 Excel 上传
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // 题库批量导入 Excel
  const [bankImportVisible, setBankImportVisible] = useState(false);
  const [bankImportUploading, setBankImportUploading] = useState(false);
  const [bankImportResult, setBankImportResult] = useState(null);
  const [bankImportFileList, setBankImportFileList] = useState([]);
  // Tabs 控制
  const [activeTab, setActiveTab] = useState('banks');
  const [banksInnerTab, setBanksInnerTab] = useState('manage');
  const [questionSearchKeyword, setQuestionSearchKeyword] = useState('');
  const [questionSearchBankIds, setQuestionSearchBankIds] = useState([]);
  const [questionSearchLoading, setQuestionSearchLoading] = useState(false);
  const [questionSearchResults, setQuestionSearchResults] = useState([]);
  const [questionSearchIndex, setQuestionSearchIndex] = useState(0);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsExam, setStatsExam] = useState(null);
  const [statsSummary, setStatsSummary] = useState(null);
  const [statsAttempts, setStatsAttempts] = useState([]);
  const [wrongPracticeModalOpen, setWrongPracticeModalOpen] = useState(false);
  const [wrongPracticeLoading, setWrongPracticeLoading] = useState(false);
  const [wrongPracticeExam, setWrongPracticeExam] = useState(null);
  const [wrongPracticeSummary, setWrongPracticeSummary] = useState(null);
  const [wrongPracticeQuestions, setWrongPracticeQuestions] = useState([]);
  const [wrongPracticeAnswers, setWrongPracticeAnswers] = useState({});
  const [wrongPracticeShowResult, setWrongPracticeShowResult] = useState(false);
  const [wrongPracticeIndex, setWrongPracticeIndex] = useState(0);
  const [answerModalOpen, setAnswerModalOpen] = useState(false);
  const [answerLoading, setAnswerLoading] = useState(false);
  const [answerAttempt, setAnswerAttempt] = useState(null);
  const [answerRows, setAnswerRows] = useState([]);
  const [answerQuestionIndex, setAnswerQuestionIndex] = useState(0);
  const [myExamsLoading, setMyExamsLoading] = useState(false);
  const [myExamsSummary, setMyExamsSummary] = useState(null);
  const [myExamsAttempts, setMyExamsAttempts] = useState([]);
  const [deleteAttemptId, setDeleteAttemptId] = useState(null);
  const [isMobile, setIsMobile] = useState(() => detectMobileView());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [form] = Form.useForm();
  const autoExamStartedRef = useRef(false);
  const swipeStartRef = useRef(null);
  const practiceSwipeStartRef = useRef(null);
  const leaveConfirmOpeningRef = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobileView());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const queryExamId = useMemo(() => {
    const search = new URLSearchParams(location.search || '');
    return Number(search.get('examId') || 0);
  }, [location.search]);
  const queryPlanId = useMemo(() => {
    const search = new URLSearchParams(location.search || '');
    return Number(search.get('planId') || 0);
  }, [location.search]);

  useEffect(() => {
    if (queryExamId > 0) {
      setActiveTab('exams');
      autoExamStartedRef.current = false;
    }
  }, [queryExamId]);

  const fetchBanks = async () => {
    if(!canView && !canAttempt) return;
    setLoadingBanks(true);
    try {
      const res = await listBanks();
      if (res.success) setBanks(res.data);
    } catch (e) { message.error('获取题库失败'); }
    setLoadingBanks(false);
  };

  useEffect(() => { fetchBanks(); }, [canView, canAttempt]);

  const openCreateBank = () => {
    form.resetFields();
    form.setFieldsValue({ bank_type: 'single', default_question_score: 1 });
    setBankModalOpen(true);
  };
  const handleCreateBank = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      const res = await createBank(values);
      if (res.success) {
        message.success('题库创建成功');
        setBankModalOpen(false);
        fetchBanks();
      } else message.error(res.message || '题库创建失败');
    } catch (e) { /* validation or request error */ }
    setCreating(false);
  };

  const handleDeleteBank = async (bank) => {
    try {
      const res = await deleteBank(bank.id);
      if (res.success) { message.success('删除成功'); fetchBanks(); }
      else message.error(res.message || '删除失败');
    } catch(e){ message.error('删除失败'); }
  };

  const selectBank = async (bank) => {
    setSelectedBank(bank);
    setLoadingQuestions(true);
    try {
      const res = await listQuestions(bank.id);
      if (res.success) setQuestions(res.data);
    } catch(e){ message.error('题目列表失败'); }
    setLoadingQuestions(false);
  };

  const handleDeleteQuestion = async (q) => {
    if(!canManage) return;
    if(!window.confirm('确定删除该题目？')) return;
    setQuestionDeleteLoadingId(q.id);
    try {
      const res = await deleteQuestion(q.id);
      if(res.success){
        message.success('题目已删除');
        // 重新加载
        selectBank(selectedBank);
      } else message.error(res.message || '删除失败');
    } catch(e){ message.error('删除异常'); }
    setQuestionDeleteLoadingId(null);
  };

  // 组合管理
  const fetchGroups = async () => {
    if(!canView) return;
    setLoadingGroups(true);
    try {
      const res = await listBankGroups();
      if(res.success) setGroups(res.data);
    } catch(e){ message.error('获取组合失败'); }
    setLoadingGroups(false);
  };
  useEffect(()=>{ fetchGroups(); }, [canView]);

  const openCreateGroup = () => {
    setEditingGroup(null);
    groupForm.resetFields();
    setGroupModalOpen(true);
  };
  const openEditGroup = (group) => {
    if (!group?.id) return;
    setEditingGroup(group);
    groupForm.resetFields();
    groupForm.setFieldsValue({
      name: group.name || '',
      description: group.description || '',
      bank_ids: Array.isArray(group.bank_ids) ? group.bank_ids : []
    });
    setGroupModalOpen(true);
  };
  const handleCreateGroup = async () => {
    try {
      const values = await groupForm.validateFields();
      const ids = values.bank_ids || [];
      if (!ids.length) {
        message.warning('请至少选择一个题库');
        return;
      }
      const payload = { name: values.name, description: values.description || '', bank_ids: ids };
      const res = editingGroup?.id ? await updateBankGroup(editingGroup.id, payload) : await createBankGroup(payload);
      if(res.success){
        message.success(editingGroup?.id ? '组合更新成功' : '组合创建成功');
        setGroupModalOpen(false);
        setEditingGroup(null);
        fetchGroups();
      } else message.error(res.message || '创建失败');
    } catch(e){ /* 验证或网络错误 */ }
  };

  const handleDeleteGroup = async (group) => {
    if (!group?.id) return;
    setGroupDeletingId(group.id);
    try {
      const res = await deleteBankGroup(group.id);
      if (res.success) {
        message.success('组合删除成功');
        fetchGroups();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败');
    }
    setGroupDeletingId(null);
  };

  // 考试管理
  const fetchExams = async () => {
    if(!canView && !canAttempt) return;
    setLoadingExams(true);
    try {
      const res = await listExams();
      if(res.success) setExams(res.data);
    } catch(e){ message.error('获取考试失败'); }
    setExamsLoaded(true);
    setLoadingExams(false);
  };
  useEffect(()=>{ fetchExams(); }, [canView, canAttempt]);

  const fetchMyExams = async () => {
    if (!canMyExams) return;
    setMyExamsLoading(true);
    try {
      const res = await getMyExams();
      if (res.success) {
        setMyExamsSummary(res.summary || null);
        setMyExamsAttempts(Array.isArray(res.attempts) ? res.attempts : []);
      } else {
        message.error(res.message || '获取我的考试失败');
        setMyExamsSummary(null);
        setMyExamsAttempts([]);
      }
    } catch (e) {
      message.error('获取我的考试失败');
      setMyExamsSummary(null);
      setMyExamsAttempts([]);
    }
    setMyExamsLoading(false);
  };

  const handleDeleteMyAttempt = async (attempt) => {
    const attemptId = Number(attempt?.attempt_id || 0);
    if (!attemptId) return;
    setDeleteAttemptId(attemptId);
    try {
      const res = await deleteAttempt(attemptId);
      if (res.success) {
        message.success('删除成功');
        fetchMyExams();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败');
    }
    setDeleteAttemptId(null);
  };
  useEffect(() => { fetchMyExams(); }, [canMyExams]);

  const openCreateExam = () => {
    setEditingExam(null);
    examForm.resetFields();
    examForm.setFieldsValue({ mode: 'fixed' });
    setExamModalOpen(true);
  };
  const openEditExam = (exam) => {
    if (!exam?.id) return;
    setEditingExam(exam);
    examForm.resetFields();
    examForm.setFieldsValue({
      title: exam.title || '',
      description: exam.description || '',
      bank_group_id: exam.bank_group_id || undefined,
      bank_ids: [],
      mode: exam.mode || 'fixed',
      single_count: exam.single_count ?? undefined,
      multiple_count: exam.multiple_count ?? undefined,
      truefalse_count: exam.truefalse_count ?? undefined,
      short_answer_count: exam.short_answer_count ?? undefined,
      duration_minutes: exam.duration_minutes ?? undefined,
      pass_score: exam.pass_score ?? undefined
    });
    setExamModalOpen(true);
  };
  const handleCreateExam = async () => {
    try {
      const values = await examForm.validateFields();
      const selectedBankIds = Array.isArray(values.bank_ids) ? values.bank_ids : [];
      if (values.mode === 'random' && !values.bank_group_id && selectedBankIds.length === 0) {
        message.warning('随机抽题模式必须选择题库或题库组合');
        return;
      }
      setCreatingExam(true);
      const payload = {
        title: values.title,
        description: values.description || '',
        bank_group_id: values.bank_group_id || null,
        bank_ids: selectedBankIds,
        single_count: values.single_count ?? null,
        multiple_count: values.multiple_count ?? null,
        truefalse_count: values.truefalse_count ?? null,
        short_answer_count: values.short_answer_count ?? null,
        mode: values.mode,
        duration_minutes: values.duration_minutes || null,
        pass_score: values.pass_score || null
      };
      const res = editingExam?.id ? await updateExam(editingExam.id, payload) : await createExam(payload);
      if(res.success){
        message.success(editingExam?.id ? '考试更新成功' : '考试创建成功');
        setExamModalOpen(false);
        setEditingExam(null);
        fetchExams();
      } else message.error(res.message || '创建失败');
    } catch(e){ /* 验证错误 */ }
    setCreatingExam(false);
  };

  const handleDeleteExam = async (exam) => {
    if (!exam?.id) return;
    setExamDeleteLoadingId(exam.id);
    try {
      const res = await deleteExam(exam.id);
      if (res.success) {
        message.success('考试已删除');
        fetchExams();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败');
    }
    setExamDeleteLoadingId(null);
  };
  const openPublishExam = (exam) => {
    if (!canManage || !exam?.id) return;
    if (String(exam.status || '').toLowerCase() === 'published') {
      const label = String(exam.publish_type || '').toLowerCase() === 'mock' ? '模拟考' : '正式考试';
      message.info(`该考试已发布（${label}）`);
      return;
    }
    setPublishTargetExam(exam);
    setPublishType(String(exam.publish_type || '').toLowerCase() === 'mock' ? 'mock' : 'formal');
    setPublishModalOpen(true);
  };

  const handlePublishExam = async () => {
    if (!publishTargetExam?.id) return;
    setPublishingId(publishTargetExam.id);
    try {
      const res = await publishExam(publishTargetExam.id, { publish_type: publishType });
      if (res.success) {
        const label = publishType === 'mock' ? '模拟考' : '正式考试';
        message.success(`考试已发布为${label}`);
        setPublishModalOpen(false);
        setPublishTargetExam(null);
        fetchExams();
      } else {
        message.error(res.message || '发布失败');
      }
    } catch (e) {
      message.error('发布异常');
    }
    setPublishingId(null);
  };

  const formatDuration = (seconds) => {
    const sec = Math.max(0, Number(seconds || 0));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}时${m}分${s}秒`;
    return `${m}分${s}秒`;
  };

  const openExamStats = async (exam) => {
    if (!exam?.id) return;
    setStatsExam(exam);
    setStatsModalOpen(true);
    setStatsLoading(true);
    try {
      const res = await getExamStats(exam.id);
      if (res.success) {
        setStatsSummary(res.summary || null);
        setStatsAttempts(Array.isArray(res.attempts) ? res.attempts : []);
      } else {
        message.error(res.message || '获取考试统计失败');
        setStatsSummary(null);
        setStatsAttempts([]);
      }
    } catch (e) {
      message.error('获取考试统计失败');
      setStatsSummary(null);
      setStatsAttempts([]);
    }
    setStatsLoading(false);
  };

  const openWrongPractice = async (exam) => {
    if (!exam?.id) return;
    setWrongPracticeExam(exam);
    setWrongPracticeSummary(null);
    setWrongPracticeQuestions([]);
    setWrongPracticeAnswers({});
    setWrongPracticeShowResult(false);
    setWrongPracticeIndex(0);
    setWrongPracticeModalOpen(true);
    setWrongPracticeLoading(true);
    try {
      const res = await getExamWrongQuestions(exam.id);
      if (res.success) {
        setWrongPracticeExam(res.exam || exam);
        setWrongPracticeSummary(res.summary || null);
        setWrongPracticeQuestions(Array.isArray(res.questions) ? res.questions : []);
      } else {
        message.error(res.message || '获取错题失败');
      }
    } catch (e) {
      message.error('获取错题失败');
    }
    setWrongPracticeLoading(false);
  };

  const openAnswerCard = async (attempt) => {
    if (!attempt?.id) return;
    setAnswerAttempt(attempt);
    setAnswerQuestionIndex(0);
    setAnswerModalOpen(true);
    setAnswerLoading(true);
    try {
      const res = await getAttempt(attempt.id);
      if (res.success) {
        setAnswerAttempt(res.attempt || attempt);
        setAnswerRows(Array.isArray(res.answers) ? res.answers : []);
        setAnswerQuestionIndex(0);
      } else {
        message.error(res.message || '获取答题卡失败');
        setAnswerRows([]);
      }
    } catch (e) {
      message.error('获取答题卡失败');
      setAnswerRows([]);
    }
    setAnswerLoading(false);
  };

  // 开始考试（创建 attempt 并拉取题目）
  const beginExam = async (exam, opts = {}) => {
    if(!canAttempt){ message.error('无作答权限'); return; }
    setActiveExam(exam); setExamLoading(true); setExamResult(null); setAnswers({}); setCurrentQuestionIndex(0);
    try {
      const planId = Number(opts?.planId || 0);
      const continueAttemptId = Number(opts?.continueAttemptId || 0);
      const startPayload = {};
      if (planId > 0) startPayload.plan_id = planId;
      if (continueAttemptId > 0) startPayload.attempt_id = continueAttemptId;
      const started = await startAttempt(exam.id, startPayload);
      if(started.success){
        setAttemptId(started.attempt_id);
        const qs = await getExamQuestions(exam.id);
        if(qs.success){
          const loadedQuestions = Array.isArray(qs.data) ? qs.data : [];
          const resumedAnswers = {};
          if (started?.resumed && Array.isArray(started.answers)) {
            started.answers.forEach((item) => {
              const qid = Number(item?.question_id || 0);
              const answerText = String(item?.answer_text || '').trim();
              if (qid > 0 && answerText) resumedAnswers[qid] = answerText;
            });
          }

          const resumedIndexRaw = Number(started?.current_question_index || 0);
          const resumedIndex = loadedQuestions.length ? Math.min(Math.max(resumedIndexRaw, 0), loadedQuestions.length - 1) : 0;
          const resumedTimeLeftRaw = Number(started?.time_left_seconds);
          const resumedTimeLeft = Number.isFinite(resumedTimeLeftRaw) && resumedTimeLeftRaw >= 0 ? resumedTimeLeftRaw : null;

          setExamQuestions(loadedQuestions);
          setAnswers(resumedAnswers);
          setCurrentQuestionIndex(resumedIndex);

          let nextTimeLeft = null;
          if (exam.duration_minutes) {
            nextTimeLeft = resumedTimeLeft != null ? resumedTimeLeft : (exam.duration_minutes * 60);
            setExamTimeLeft(nextTimeLeft);
          } else {
            setExamTimeLeft(null);
          }

          if (started?.resumed && String(exam?.publish_type || '').toLowerCase() === 'mock') {
            message.success('已恢复模拟考进度');
          }
        } else message.error(qs.message || '题目加载失败');
      } else message.error(started.message || '开始失败');
    } catch(e){ message.error('开始考试异常'); }
    setExamLoading(false);
  };

  useEffect(() => {
    if (queryExamId <= 0) return;
    if (autoExamStartedRef.current) return;
    if (loadingExams || !examsLoaded) return;
    if (activeExam) {
      autoExamStartedRef.current = true;
      return;
    }

    const target = exams.find((e) => Number(e?.id) === queryExamId);
    if (!target) {
      autoExamStartedRef.current = true;
      message.warning('未找到关联考试');
      return;
    }
    if (!canAttempt) {
      autoExamStartedRef.current = true;
      message.warning('当前账号无考试作答权限');
      return;
    }
    if (String(target?.status || '').toLowerCase() !== 'published') {
      autoExamStartedRef.current = true;
      message.warning('关联考试尚未发布，暂不能参加');
      return;
    }

    autoExamStartedRef.current = true;
    beginExam(target, { planId: queryPlanId });
  }, [queryExamId, queryPlanId, exams, loadingExams, examsLoaded, canAttempt, activeExam]);

  // 倒计时副作用
  useEffect(()=>{
    if(examTimeLeft == null) return;
    if(examResult) return; // 已交卷停止
    if(examTimeLeft <= 0){ submitExam(); return; }
    const t = setTimeout(()=> setExamTimeLeft(examTimeLeft - 1), 1000);
    return ()=> clearTimeout(t);
  }, [examTimeLeft, examResult]);

  const submitExam = async () => {
    if(!attemptId) return;
    setExamSubmitting(true);
    try {
      const payloadAnswers = Object.entries(answers).map(([qid,val]) => ({ question_id: parseInt(qid), selected: val }));
      const res = await submitAttempt(attemptId, payloadAnswers);
      if(res.success){ setExamResult(res); message.success('提交成功'); fetchMyExams(); }
      else message.error(res.message || '提交失败');
    } catch(e){ message.error('提交异常'); }
    setExamSubmitting(false);
  };

  const isMockExam = () => String(activeExam?.publish_type || '').toLowerCase() === 'mock';
  const hasRunningExam = () => !!activeExam && !examResult;

  const clearExamState = () => {
    setActiveExam(null);
    setExamQuestions([]);
    setAttemptId(null);
    setAnswers({});
    setExamResult(null);
    setExamTimeLeft(null);
    setCurrentQuestionIndex(0);
    swipeStartRef.current = null;
  };

  const resetExamState = ({ refreshMyExams = true } = {}) => {
    clearExamState();
    if (refreshMyExams) {
      fetchMyExams();
    }
    return true;
  };

  const askLeaveExamConfirm = () => new Promise((resolve) => {
    const mockExam = isMockExam();
    Modal.confirm({
      title: mockExam ? '退出模拟考' : '退出正式考试',
      content: mockExam
        ? '退出后不会自动保存草稿，当前未提交答案将丢失。是否确认退出？'
        : '退出后当前未提交答案将丢失。是否确认退出？',
      okText: '确认退出',
      cancelText: '继续考试',
      onOk: () => {
        resetExamState({ refreshMyExams: true });
        resolve(true);
      },
      onCancel: () => resolve(false)
    });
  });

  const ensureCanLeaveExam = async () => {
    if (!hasRunningExam()) return true;
    if (leaveConfirmOpeningRef.current) return false;
    leaveConfirmOpeningRef.current = true;
    try {
      return await askLeaveExamConfirm();
    } finally {
      leaveConfirmOpeningRef.current = false;
    }
  };

  const handleExitExam = async () => {
    if (!activeExam) return;
    if (!hasRunningExam()) {
      resetExamState({ refreshMyExams: true });
      return;
    }
    await ensureCanLeaveExam();
  };

  const handleMainTabChange = async (nextTabKey) => {
    if (nextTabKey === activeTab) return;
    if (nextTabKey !== 'exams') {
      const canLeave = await ensureCanLeaveExam();
      if (!canLeave) return;
    }
    setActiveTab(nextTabKey);
  };

  useEffect(() => {
    if (!hasRunningExam()) return undefined;
    const beforeUnloadHandler = (event) => {
      event.preventDefault();
      event.returnValue = '考试进行中，离开后未提交答案将丢失。';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, [activeExam, examResult]);

  useEffect(() => {
    if (!hasRunningExam()) return undefined;
    const handleGlobalLinkClick = async (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest('a[href]');
      if (!anchor) return;
      if (anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

      const nextUrl = new URL(href, window.location.origin);
      if (nextUrl.origin !== window.location.origin) return;

      const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const nextPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      if (nextPath === currentPath) return;

      event.preventDefault();
      event.stopPropagation();
      const canLeave = await ensureCanLeaveExam();
      if (canLeave) {
        navigate(nextPath);
      }
    };

    document.addEventListener('click', handleGlobalLinkClick, true);
    return () => {
      document.removeEventListener('click', handleGlobalLinkClick, true);
    };
  }, [activeExam, examResult, navigate]);

  const renderTimer = () => {
    if(examTimeLeft == null) return null; const m=Math.floor(examTimeLeft/60), s=examTimeLeft%60; const danger=examTimeLeft<=60;
    return <span style={{ fontWeight:600, color: danger? '#dc2626':'#2563eb' }}>{m}:{s.toString().padStart(2,'0')}</span>;
  };

  const goToPrevQuestion = () => {
    setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNextQuestion = () => {
    if (!examQuestions.length) return;
    setCurrentQuestionIndex((prev) => Math.min(examQuestions.length - 1, prev + 1));
  };

  const goToQuestion = (index) => {
    if (!examQuestions.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), examQuestions.length - 1);
    setCurrentQuestionIndex(safeIndex);
  };

  const continueMockExam = async (record) => {
    const examId = Number(record?.exam_id || 0);
    const attemptIdFromRecord = Number(record?.attempt_id || 0);
    if (!examId || !attemptIdFromRecord) {
      message.warning('继续考试失败：记录信息不完整');
      return;
    }

    const targetExam = exams.find((item) => Number(item?.id) === examId) || {
      id: examId,
      title: record?.exam_title || `考试#${examId}`,
      duration_minutes: record?.duration_minutes || null,
      publish_type: record?.publish_type || 'mock',
      status: 'published'
    };

    setActiveTab('exams');
    await beginExam(targetExam, {
      planId: Number(record?.plan_id || 0),
      continueAttemptId: attemptIdFromRecord
    });
  };

  const handleQuestionTouchStart = (event) => {
    if (!isMobile) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleQuestionTouchEnd = (event) => {
    if (!isMobile || !swipeStartRef.current) return;
    const touch = event.changedTouches?.[0];
    if (!touch) {
      swipeStartRef.current = null;
      return;
    }

    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    const threshold = 50;
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX <= -threshold) {
      goToNextQuestion();
    } else if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX >= threshold) {
      goToPrevQuestion();
    }
    swipeStartRef.current = null;
  };

  const handleQuestionMouseDown = (event) => {
    if (!isMobile) return;
    swipeStartRef.current = { x: event.clientX, y: event.clientY };
  };

  const handleQuestionMouseUp = (event) => {
    if (!isMobile || !swipeStartRef.current) return;
    const deltaX = event.clientX - swipeStartRef.current.x;
    const deltaY = event.clientY - swipeStartRef.current.y;
    const threshold = 50;
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX <= -threshold) {
      goToNextQuestion();
    } else if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX >= threshold) {
      goToPrevQuestion();
    }
    swipeStartRef.current = null;
  };

  const renderExamQuestion = (q, idx) => (
    <div key={q.id} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #e5e7eb' }}>
      <div style={{ fontWeight: 500, marginBottom: 8, fontSize: isMobile ? 15 : 19, lineHeight: 1.6 }}>{idx + 1}. {q.question_text}</div>
      <Space direction='vertical' style={{ width: '100%' }}>
        {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((opt) => {
          const text = q[`option_${opt.toLowerCase()}`];
          if (!text) return null;
          const selected = answers[q.id] === opt;
          const disabled = !!examResult;
          return (
            <Button
              key={opt}
              size={isMobile ? 'middle' : 'large'}
              type={selected ? 'primary' : 'default'}
              disabled={disabled}
              block
              style={{ textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal', height: 'auto', minHeight: isMobile ? 40 : 50, fontSize: isMobile ? 14 : 17 }}
              onClick={() => {
                if (disabled) return;
                setAnswers((a) => ({ ...a, [q.id]: opt }));
              }}
            >
              {opt}. {text}
            </Button>
          );
        })}
      </Space>
    </div>
  );

  const answeredExamCount = examQuestions.reduce((count, q) => (answers[q.id] ? count + 1 : count), 0);

  const normalizeAnswerText = (value) => {
    return String(value || '')
      .toUpperCase()
      .replace(/[，；;\s]+/g, ',')
      .replace(/,+/g, ',')
      .replace(/^,|,$/g, '');
  };

  const toAnswerSet = (value) => {
    const normalized = normalizeAnswerText(value);
    if (!normalized) return new Set();
    return new Set(normalized.split(',').filter(Boolean));
  };

  const isSameAnswerSet = (left, right) => {
    if (!left || !right) return false;
    if (left.size !== right.size) return false;
    for (const item of left) {
      if (!right.has(item)) return false;
    }
    return true;
  };

  const getWrongPracticeJudge = (q) => {
    const selectedSet = toAnswerSet(wrongPracticeAnswers[q?.id]);
    const correctSet = toAnswerSet(q?.correct_answer);
    const answered = selectedSet.size > 0;
    const isCorrect = answered && isSameAnswerSet(selectedSet, correctSet);
    const isWrong = answered && !isCorrect;
    return { selectedSet, correctSet, answered, isCorrect, isWrong };
  };

  const goToPrevAnswerQuestion = () => {
    setAnswerQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNextAnswerQuestion = () => {
    setAnswerQuestionIndex((prev) => Math.min(Math.max(answerRows.length - 1, 0), prev + 1));
  };

  const goToAnswerQuestion = (index) => {
    if (!answerRows.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), answerRows.length - 1);
    setAnswerQuestionIndex(safeIndex);
  };

  const goToPrevWrongPractice = () => {
    setWrongPracticeIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNextWrongPractice = () => {
    setWrongPracticeIndex((prev) => Math.min(Math.max(wrongPracticeQuestions.length - 1, 0), prev + 1));
  };

  const goToWrongPracticeQuestion = (index) => {
    if (!wrongPracticeQuestions.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), wrongPracticeQuestions.length - 1);
    setWrongPracticeIndex(safeIndex);
  };

  const renderWrongPracticeQuestion = (q, idx) => {
    if (!q) return null;
    const { selectedSet, correctSet } = getWrongPracticeJudge(q);

    return (
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #e5e7eb' }}>
        <div style={{ fontWeight: 500, marginBottom: 6, fontSize: isMobile ? 15 : 18, lineHeight: 1.6 }}>{idx + 1}. {q.question_text}</div>
        <div style={{ marginBottom: 8, fontSize: 12, color: '#64748b' }}>
          错答次数：{q.wrong_count || 0}，涉及人数：{q.wrong_attempt_count || 0}
        </div>
        <Space direction='vertical' style={{ width: '100%' }}>
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((opt) => {
            const text = q[`option_${opt.toLowerCase()}`];
            if (!text) return null;
            const isSelected = selectedSet.has(opt);
            const isCorrect = correctSet.has(opt);

            let style = { textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal', height: 'auto', minHeight: isMobile ? 40 : 48, fontSize: isMobile ? 14 : 16 };
            if (wrongPracticeShowResult && isCorrect) {
              style = { ...style, borderColor: '#16a34a', background: '#f0fdf4', color: '#14532d' };
            } else if (wrongPracticeShowResult && isSelected && !isCorrect) {
              style = { ...style, borderColor: '#ef4444', background: '#fef2f2', color: '#991b1b' };
            }

            return (
              <Button
                key={opt}
                size={isMobile ? 'middle' : 'large'}
                type={isSelected && !wrongPracticeShowResult ? 'primary' : 'default'}
                block
                style={style}
                onClick={() => {
                  if (wrongPracticeShowResult) return;
                  setWrongPracticeAnswers((prev) => ({ ...prev, [q.id]: opt }));
                }}
              >
                {opt}. {text}
              </Button>
            );
          })}
        </Space>
        {wrongPracticeShowResult && (
          <div style={{ marginTop: 8, fontSize: 13, color: '#334155' }}>
            <span>你的答案：{wrongPracticeAnswers[q.id] || '未答'}</span>
            <span style={{ marginLeft: 10 }}>正确答案：{q.correct_answer || '-'}</span>
            {q.answer_explanation ? <span style={{ marginLeft: 10, color: '#0369a1' }}>解析：{q.answer_explanation}</span> : null}
          </div>
        )}
      </div>
    );
  };

  const wrongPracticeCurrentQuestion = wrongPracticeQuestions[wrongPracticeIndex] || null;
  const wrongPracticeAnsweredCount = wrongPracticeQuestions.reduce((count, q) => (wrongPracticeAnswers[q.id] ? count + 1 : count), 0);

  useEffect(() => {
    if (answerQuestionIndex >= answerRows.length && answerRows.length > 0) {
      setAnswerQuestionIndex(answerRows.length - 1);
    }
  }, [answerRows, answerQuestionIndex]);

  const renderAnswerReviewQuestion = (row, idx) => {
    if (!row) return null;
    const selectedSet = toAnswerSet(row.answer_text);
    const correctSet = toAnswerSet(row.correct_answer);
    const isCorrect = Number(row.is_correct) === 1;
    const isWrong = Number(row.is_correct) === 0;

    return (
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #e5e7eb' }}>
        <div style={{ fontWeight: 500, marginBottom: 8, fontSize: isMobile ? 15 : 19, lineHeight: 1.6 }}>{idx + 1}. {row.question_text}</div>
        <Space direction='vertical' style={{ width: '100%' }}>
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((opt) => {
            const text = row[`option_${opt.toLowerCase()}`];
            if (!text) return null;
            const selected = selectedSet.has(opt);
            const correct = correctSet.has(opt);

            let style = { textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal', height: 'auto', minHeight: isMobile ? 40 : 50, fontSize: isMobile ? 14 : 17 };
            if (selected && !correct) {
              style = { ...style, borderColor: '#ef4444', background: '#fef2f2', color: '#991b1b' };
            } else if (correct) {
              style = { ...style, borderColor: '#16a34a', background: '#f0fdf4', color: '#14532d' };
            }

            return (
              <Button
                key={opt}
                size={isMobile ? 'middle' : 'large'}
                type={selected && correct ? 'primary' : 'default'}
                block
                style={style}
              >
                {opt}. {text}
              </Button>
            );
          })}
        </Space>
        <div style={{ marginTop: 8, fontSize: 13, color: '#334155' }}>
          <span>你的答案：{row.answer_text || '未答'}</span>
          <span style={{ marginLeft: 10 }}>正确答案：{row.correct_answer || '-'}</span>
          <span style={{ marginLeft: 10, fontWeight: 600, color: isCorrect ? '#16a34a' : (isWrong ? '#dc2626' : '#64748b') }}>
            {isCorrect ? '判定：正确' : (isWrong ? '判定：错误' : '判定：未判定')}
          </span>
        </div>
      </div>
    );
  };

  const answerWrongCount = answerRows.filter((row) => Number(row?.is_correct) === 0).length;
  const answerCurrentRow = answerRows[answerQuestionIndex] || null;

  useEffect(() => {
    if (practiceCurrentQuestionIndex >= practiceQuestions.length && practiceQuestions.length > 0) {
      setPracticeCurrentQuestionIndex(practiceQuestions.length - 1);
    }
  }, [practiceQuestions, practiceCurrentQuestionIndex]);

  useEffect(() => {
    if (practiceRecordIndex >= practiceRecordRows.length && practiceRecordRows.length > 0) {
      setPracticeRecordIndex(practiceRecordRows.length - 1);
    }
  }, [practiceRecordRows, practiceRecordIndex]);

  const renderPracticeRecordReviewQuestion = (row, idx) => {
    if (!row) return null;
    const selectedSet = toAnswerSet(row.answer_text);
    const correctSet = toAnswerSet(row.correct_answer);
    const isCorrect = Number(row.is_correct) === 1;
    const isWrong = Number(row.is_correct) === 0;

    return (
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #e5e7eb' }}>
        <div style={{ fontWeight: 500, marginBottom: 8, fontSize: isMobile ? 15 : 19, lineHeight: 1.6 }}>{idx + 1}. {row.question_text}</div>
        <Space direction='vertical' style={{ width: '100%' }}>
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((opt) => {
            const text = row[`option_${opt.toLowerCase()}`];
            if (!text) return null;
            const selected = selectedSet.has(opt);
            const correct = correctSet.has(opt);

            let style = { textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal', height: 'auto', minHeight: isMobile ? 40 : 50, fontSize: isMobile ? 14 : 17 };
            if (selected && !correct) {
              style = { ...style, borderColor: '#ef4444', background: '#fef2f2', color: '#991b1b' };
            } else if (correct) {
              style = { ...style, borderColor: '#16a34a', background: '#f0fdf4', color: '#14532d' };
            }

            return (
              <Button
                key={opt}
                size={isMobile ? 'middle' : 'large'}
                type={selected && correct ? 'primary' : 'default'}
                block
                style={style}
              >
                {opt}. {text}
              </Button>
            );
          })}
        </Space>
        <div style={{ marginTop: 8, fontSize: 13, color: '#334155' }}>
          <span>你的答案：{row.answer_text || '未答'}</span>
          <span style={{ marginLeft: 10 }}>正确答案：{row.correct_answer || '-'}</span>
          <span style={{ marginLeft: 10, fontWeight: 600, color: isCorrect ? '#16a34a' : (isWrong ? '#dc2626' : '#64748b') }}>
            {isCorrect ? '判定：正确' : (isWrong ? '判定：错误' : '判定：未判定')}
          </span>
        </div>
      </div>
    );
  };

  // 练习模式逻辑
  const fetchPracticeRecords = async () => {
    if (!canAttempt) return;
    setPracticeRecordsLoading(true);
    try {
      const res = await getMyPracticeRecords();
      if (res.success) {
        setPracticeRecordsSummary(res.summary || null);
        setPracticeRecords(Array.isArray(res.sessions) ? res.sessions : []);
      } else {
        message.error(res.message || '获取练习记录失败');
        setPracticeRecordsSummary(null);
        setPracticeRecords([]);
      }
    } catch (e) {
      message.error('获取练习记录失败');
      setPracticeRecordsSummary(null);
      setPracticeRecords([]);
    }
    setPracticeRecordsLoading(false);
  };

  useEffect(() => {
    fetchPracticeRecords();
  }, [canAttempt]);

  const executePractice = async () => {
    if(!practiceBanks.length){ message.warning('请选择题库'); return; }
    setPracticeLoading(true);
    try {
      const res = await practice(practiceBanks, 30, true);
      if(res.success){
        setPracticeSessionId(Number(res.practice_session_id || 0) || null);
        setPracticeQuestions(Array.isArray(res.data) ? res.data : []);
        setPracticeAnswers({});
        setPracticeShowResult(false);
        setPracticeSummary(null);
        setPracticeCurrentQuestionIndex(0);
        setPracticeInnerTab('select');
      }
      else message.error(res.message || '获取题目失败');
    } catch(e){ message.error('练习题获取异常'); }
    setPracticeLoading(false);
  };

  const finishPractice = async () => {
    if (!practiceQuestions.length) return;
    setPracticeSubmitting(true);
    try {
      const payloadAnswers = Object.entries(practiceAnswers).map(([qid, val]) => ({ question_id: parseInt(qid, 10), selected: val }));
      if (practiceSessionId) {
        const res = await submitPracticeSession(practiceSessionId, payloadAnswers);
        if (res.success) {
          setPracticeSummary(res.summary || null);
          fetchPracticeRecords();
        } else {
          message.warning(res.message || '保存练习记录失败');
        }
      }
      setPracticeShowResult(true);
    } catch (e) {
      message.warning('保存练习记录失败');
      setPracticeShowResult(true);
    }
    setPracticeSubmitting(false);
  };

  const resetPracticeRun = () => {
    setPracticeShowResult(false);
    setPracticeAnswers({});
    setPracticeSummary(null);
    setPracticeCurrentQuestionIndex(0);
  };

  const goToPrevPracticeQuestion = () => {
    setPracticeCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNextPracticeQuestion = () => {
    setPracticeCurrentQuestionIndex((prev) => Math.min(Math.max(practiceQuestions.length - 1, 0), prev + 1));
  };

  const goToPracticeQuestion = (index) => {
    if (!practiceQuestions.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), practiceQuestions.length - 1);
    setPracticeCurrentQuestionIndex(safeIndex);
  };

  const handlePracticeTouchStart = (event) => {
    if (!isMobile) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    practiceSwipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handlePracticeTouchEnd = (event) => {
    if (!isMobile || !practiceSwipeStartRef.current) return;
    const touch = event.changedTouches?.[0];
    if (!touch) {
      practiceSwipeStartRef.current = null;
      return;
    }
    const deltaX = touch.clientX - practiceSwipeStartRef.current.x;
    const deltaY = touch.clientY - practiceSwipeStartRef.current.y;
    const threshold = 50;
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX <= -threshold) {
      goToNextPracticeQuestion();
    } else if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX >= threshold) {
      goToPrevPracticeQuestion();
    }
    practiceSwipeStartRef.current = null;
  };

  const handlePracticeMouseDown = (event) => {
    if (!isMobile) return;
    practiceSwipeStartRef.current = { x: event.clientX, y: event.clientY };
  };

  const handlePracticeMouseUp = (event) => {
    if (!isMobile || !practiceSwipeStartRef.current) return;
    const deltaX = event.clientX - practiceSwipeStartRef.current.x;
    const deltaY = event.clientY - practiceSwipeStartRef.current.y;
    const threshold = 50;
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX <= -threshold) {
      goToNextPracticeQuestion();
    } else if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX >= threshold) {
      goToPrevPracticeQuestion();
    }
    practiceSwipeStartRef.current = null;
  };

  const renderPracticeQuestion = (q, idx) => {
    if (!q) return null;
    const selectedSet = toAnswerSet(practiceAnswers[q.id]);
    const correctSet = toAnswerSet(q.correct_answer);

    return (
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #e5e7eb' }}>
        <div style={{ fontWeight: 500, marginBottom: 8, fontSize: isMobile ? 15 : 19, lineHeight: 1.6 }}>{idx + 1}. {q.question_text}</div>
        <Space direction='vertical' style={{ width: '100%' }}>
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((opt) => {
            const text = q[`option_${opt.toLowerCase()}`];
            if (!text) return null;

            const selected = selectedSet.has(opt);
            const correct = correctSet.has(opt);

            let style = { textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal', height: 'auto', minHeight: isMobile ? 40 : 50, fontSize: isMobile ? 14 : 17 };
            if (practiceShowResult && selected && !correct) {
              style = { ...style, borderColor: '#ef4444', background: '#fef2f2', color: '#991b1b' };
            } else if (practiceShowResult && correct) {
              style = { ...style, borderColor: '#16a34a', background: '#f0fdf4', color: '#14532d' };
            }

            return (
              <Button
                key={opt}
                size={isMobile ? 'middle' : 'large'}
                type={selected && !practiceShowResult ? 'primary' : 'default'}
                block
                style={style}
                onClick={() => {
                  if (practiceShowResult) return;
                  setPracticeAnswers((prev) => ({ ...prev, [q.id]: opt }));
                }}
              >
                {opt}. {text}
              </Button>
            );
          })}
        </Space>
        {practiceShowResult && (
          <div style={{ marginTop: 8, fontSize: 13, color: '#334155' }}>
            <span>你的答案：{practiceAnswers[q.id] || '未答'}</span>
            <span style={{ marginLeft: 10 }}>正确答案：{q.correct_answer || '-'}</span>
            {q.answer_explanation ? <span style={{ marginLeft: 10, color: '#0369a1' }}>解析：{q.answer_explanation}</span> : null}
          </div>
        )}
      </div>
    );
  };

  const openPracticeRecordCard = async (session) => {
    const sessionId = Number(session?.session_id || session?.id || 0);
    if (!sessionId) return;

    setPracticeRecordModalOpen(true);
    setPracticeRecordLoading(true);
    setPracticeRecordSession(session || null);
    setPracticeRecordRows([]);
    setPracticeRecordIndex(0);
    try {
      const res = await getPracticeSession(sessionId);
      if (res.success) {
        setPracticeRecordSession(res.session || session || null);
        setPracticeRecordRows(Array.isArray(res.answers) ? res.answers : []);
      } else {
        message.error(res.message || '获取练习答题卡失败');
      }
    } catch (e) {
      message.error('获取练习答题卡失败');
    }
    setPracticeRecordLoading(false);
  };

  const handleDeletePracticeRecord = async (record) => {
    const sessionId = Number(record?.session_id || record?.id || 0);
    if (!sessionId) return;

    setDeletePracticeSessionId(sessionId);
    try {
      const res = await deletePracticeSession(sessionId);
      if (res.success) {
        message.success('练习记录已删除');
        if (Number(practiceRecordSession?.session_id || 0) === sessionId) {
          setPracticeRecordModalOpen(false);
          setPracticeRecordSession(null);
          setPracticeRecordRows([]);
          setPracticeRecordIndex(0);
        }
        fetchPracticeRecords();
      } else {
        message.error(res.message || '删除练习记录失败');
      }
    } catch (e) {
      message.error('删除练习记录失败');
    }
    setDeletePracticeSessionId(null);
  };

  const goToPrevPracticeRecord = () => {
    setPracticeRecordIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNextPracticeRecord = () => {
    setPracticeRecordIndex((prev) => Math.min(Math.max(practiceRecordRows.length - 1, 0), prev + 1));
  };

  const goToPracticeRecordQuestion = (index) => {
    if (!practiceRecordRows.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), practiceRecordRows.length - 1);
    setPracticeRecordIndex(safeIndex);
  };

  const practiceCurrentQuestion = practiceQuestions[practiceCurrentQuestionIndex] || null;
  const practiceAnsweredCount = practiceQuestions.reduce((count, q) => (practiceAnswers[q.id] ? count + 1 : count), 0);
  const practiceRecordWrongCount = practiceRecordRows.filter((row) => Number(row?.is_correct) === 0).length;
  const practiceRecordCurrentRow = practiceRecordRows[practiceRecordIndex] || null;

  const formatDate = (d) => {
    if(!d) return '-';
    try { return new Date(d).toLocaleString('zh-CN', { hour12:false }); } catch { return d; }
  };

  const formatTimeOnly = (value) => {
    if (!value) return '-';
    const raw = String(value).trim();
    const match = raw.match(/(\d{2}:\d{2}:\d{2})/);
    if (match) return match[1];
    return raw;
  };
  const bankColumns = [
    { title: '编号', dataIndex: 'id', width: 70 },
    { title: '名称', dataIndex: 'name' },
    { title: '题库类型', dataIndex: 'bank_type', width: 110, render: (v)=> v || 'single' },
    { title: '单题分值', dataIndex: 'default_question_score', width: 90, render: (v)=> (v ?? 1) },
    { title: '添加时间', dataIndex: 'created_at', width: 160, render: (v)=>formatDate(v) },
    { title: '题目数量', dataIndex: 'question_count', width: 100, render: (v)=> (v!=null? v : '-') },
    { title: '操作', width: 200, render: (_, record) => (
      <Space>
        <Button size='small' type='link' onClick={() => selectBank(record)}>管理</Button>
        <Button size='small' type='link' onClick={() => {
          setPracticeBanks(p => p.includes(record.id) ? p : [...p, record.id]);
          setPracticeInnerTab('select');
          setActiveTab('practice');
        }}>练习</Button>
        {canManage && <Button size='small' type='link' onClick={() => { setSelectedBank(record); setUploadVisible(true); }}>上传题目</Button>}
        {canManage && <Popconfirm title='确定删除该题库及题目?' onConfirm={() => handleDeleteBank(record)}><Button danger size='small' type='link'>删除</Button></Popconfirm>}
      </Space>
    )}
  ];

  const questionColumns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '题型', dataIndex: 'question_type', width: 80 },
    { title: '题干', dataIndex: 'question_text' },
    { title: 'A', dataIndex: 'option_a', width: 110 },
    { title: 'B', dataIndex: 'option_b', width: 110 },
    { title: 'C', dataIndex: 'option_c', width: 110 },
    { title: 'D', dataIndex: 'option_d', width: 110 },
    { title: 'E', dataIndex: 'option_e', width: 110 },
    { title: 'F', dataIndex: 'option_f', width: 110 },
    { title: 'G', dataIndex: 'option_g', width: 110 },
    { title: 'H', dataIndex: 'option_h', width: 110 },
    { title: '分值', dataIndex: 'score', width: 70 },
    { title: '知识类别', dataIndex: 'knowledge_category', width: 120 },
    { title: '重要等级', dataIndex: 'importance_level', width: 100 },
    { title: '分类', dataIndex: 'question_category', width: 120 },
    canManage ? { title: '操作', width: 80, fixed:'right', render: (_, r) => <Button danger size='small' loading={questionDeleteLoadingId===r.id} onClick={()=>handleDeleteQuestion(r)}>删除</Button> } : null
  ].filter(Boolean);

  // Excel 上传占位（后端已支持）
  const [fileList, setFileList] = useState([]);
  const uploadProps = {
    accept: '.xlsx,.xls',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      setFileList([file]);
      return false; // 阻止自动上传
    },
    onRemove: () => setFileList([])
  };

  // 题库导入 Upload
  const bankImportUploadProps = {
    accept: '.xlsx,.xls',
    multiple: false,
    fileList: bankImportFileList,
    beforeUpload: (file) => { setBankImportFileList([file]); return false; },
    onRemove: () => setBankImportFileList([])
  };

  const handleDoUpload = async () => {
    if(!selectedBank) { message.warning('未选择题库'); return; }
    if(!fileList.length){ message.warning('请选择文件'); return; }
    setUploading(true);
    try {
      const json = await uploadQuestionsExcel(selectedBank.id, fileList[0]);
      if(json.success){
        setUploadResult(json);
        message.success(`上传完成: 新增 ${json.inserted}, 更新 ${json.updated}, 跳过 ${json.skipped}`);
        selectBank(selectedBank); // refresh questions
      } else {
        message.error(json.message || '上传失败');
      }
    } catch(e){ message.error('上传异常'); }
    setUploading(false);
  };

  const handleDoBankImport = async () => {
    if(!bankImportFileList.length){ message.warning('请选择文件'); return; }
    setBankImportUploading(true);
    try {
      const json = await importBanksExcel(bankImportFileList[0]);
      if(json.success){
        setBankImportResult(json);
        message.success(`导入完成: 新增 ${json.inserted}, 更新 ${json.updated}, 跳过 ${json.skipped}`);
        fetchBanks();
      } else message.error(json.message || '导入失败');
    } catch(e){ message.error('导入异常'); }
    setBankImportUploading(false);
  };

  const executeQuestionSearch = async () => {
    const keyword = String(questionSearchKeyword || '').trim();
    const bankIds = Array.isArray(questionSearchBankIds) ? questionSearchBankIds : [];
    if (!keyword && !bankIds.length) {
      message.warning('请输入关键词或选择题库');
      return;
    }

    setQuestionSearchLoading(true);
    try {
      const res = await searchQuestions({ keyword, bank_ids: bankIds.join(','), limit: 300 });
      if (res.success) {
        const rows = Array.isArray(res.data) ? res.data : [];
        setQuestionSearchResults(rows);
        setQuestionSearchIndex(0);
        if (!rows.length) message.info('未搜索到匹配题目');
      } else {
        message.error(res.message || '搜索题目失败');
      }
    } catch (e) {
      message.error('搜索题目失败');
    }
    setQuestionSearchLoading(false);
  };

  const resetQuestionSearch = () => {
    setQuestionSearchKeyword('');
    setQuestionSearchBankIds([]);
    setQuestionSearchResults([]);
    setQuestionSearchIndex(0);
  };

  const goToPrevQuestionSearch = () => {
    setQuestionSearchIndex((prev) => Math.max(0, prev - 1));
  };

  const goToNextQuestionSearch = () => {
    setQuestionSearchIndex((prev) => Math.min(Math.max(questionSearchResults.length - 1, 0), prev + 1));
  };

  const goToQuestionSearchIndex = (index) => {
    if (!questionSearchResults.length) return;
    const safeIndex = Math.min(Math.max(Number(index) || 0, 0), questionSearchResults.length - 1);
    setQuestionSearchIndex(safeIndex);
  };

  const renderQuestionSearchCard = (row, idx) => {
    if (!row) return null;
    const correctSet = toAnswerSet(row.correct_answer);

    return (
      <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px dashed #e5e7eb' }}>
        <div style={{ fontWeight: 500, marginBottom: 8, fontSize: isMobile ? 15 : 18, lineHeight: 1.6 }}>
          {idx + 1}. {row.question_text}
        </div>
        <div style={{ marginBottom: 8, fontSize: 12, color: '#64748b' }}>
          题库：{row.bank_name || `#${row.bank_id || '-'}`}，题型：{row.question_type || '-'}
        </div>
        <Space direction='vertical' style={{ width: '100%' }}>
          {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((opt) => {
            const text = row[`option_${opt.toLowerCase()}`];
            if (!text) return null;
            const correct = correctSet.has(opt);
            const style = {
              textAlign: 'left',
              justifyContent: 'flex-start',
              whiteSpace: 'normal',
              height: 'auto',
              minHeight: isMobile ? 40 : 50,
              fontSize: isMobile ? 14 : 17,
              ...(correct ? { borderColor: '#16a34a', background: '#f0fdf4', color: '#14532d' } : {})
            };

            return (
              <Button
                key={opt}
                size={isMobile ? 'middle' : 'large'}
                type='default'
                block
                style={style}
              >
                {opt}. {text}
              </Button>
            );
          })}
        </Space>
        <div style={{ marginTop: 8, fontSize: 13, color: '#334155' }}>
          <span>正确答案：{row.correct_answer || '-'}</span>
          {row.answer_explanation ? <span style={{ marginLeft: 10, color: '#0369a1' }}>解析：{row.answer_explanation}</span> : null}
        </div>
      </div>
    );
  };

  const questionSearchCurrent = questionSearchResults[questionSearchIndex] || null;

  const BanksManageView = (
    <div>
      <Space style={{ marginBottom: 16, flexWrap:'wrap' }}>
  <Button type='primary' icon={<PlusOutlined />} onClick={() => { if(!canManage){ message.warning('无权限创建题库'); return; } openCreateBank(); }}>新建题库</Button>
        {canManage && <Button onClick={openCreateGroup}>新建组合</Button>}
        {canManage && <Button onClick={openCreateExam}>新建考试</Button>}
        {canManage && <Button onClick={()=>setBankImportVisible(true)}>题库导入</Button>}
        {canManage && <Button onClick={()=> setUploadVisible(true)} type='default'>Excel上传题目</Button>}
      </Space>
      <Table rowKey='id' loading={loadingBanks} dataSource={banks} columns={bankColumns} size='small' pagination={{ pageSize: 10 }} />
      <div style={{ marginTop: 32 }}>
        <h3 style={{ marginBottom: 8 }}>题库组合</h3>
        <Table rowKey='id' loading={loadingGroups} dataSource={groups} size='small' pagination={{ pageSize: 5 }} columns={[{ title: 'ID', dataIndex: 'id', width: 70 },{ title: '名称', dataIndex: 'name' },{ title: '题库集合', render: (_, r) => Array.isArray(r.bank_ids) ? r.bank_ids.join(', ') : (r.bank_ids || '-'), width: 260 }, canManage ? { title: '操作', width: 170, render: (_, r) => (
          <Space>
            <Button size='small' onClick={() => openEditGroup(r)}>编辑</Button>
            <Popconfirm title='确定删除该题库组合？' onConfirm={() => handleDeleteGroup(r)}>
              <Button size='small' danger loading={groupDeletingId===r.id}>删除</Button>
            </Popconfirm>
          </Space>
        ) } : null].filter(Boolean)} />
      </div>
      <div style={{ marginTop: 32 }}>
        <h3 style={{ marginBottom: 8 }}>考试列表</h3>
        <Table rowKey='id' loading={loadingExams} dataSource={exams} size='small' pagination={{ pageSize: 8 }} columns={[{ title: 'ID', dataIndex: 'id', width: 70 },{ title: '标题', dataIndex: 'title' },{ title: '模式', dataIndex: 'mode', width: 90 },{ title: '状态', dataIndex: 'status', width: 90 },{ title: '发布类型', dataIndex: 'publish_type', width: 100, render: (v, r) => (String(r?.status || '').toLowerCase() === 'published' ? (String(v || '').toLowerCase() === 'mock' ? '模拟考' : '正式考试') : '-') },{ title: '及格分', dataIndex: 'pass_score', width: 90 }, canStats ? { title: '统计', width: 90, render: (_, r) => <Button size='small' onClick={()=>openExamStats(r)}>统计</Button> } : null, canManage?{ title: '操作', width: 260, render: (_, r) => (
          <Space>
            <Button size='small' onClick={()=>openEditExam(r)}>编辑</Button>
            <Button size='small' loading={publishingId===r.id} disabled={r.status==='published'} onClick={()=>openPublishExam(r)}>发布</Button>
            <Popconfirm title='确定删除该考试？相关作答记录将一并删除' onConfirm={()=>handleDeleteExam(r)}>
              <Button danger size='small' loading={examDeleteLoadingId===r.id}>删除</Button>
            </Popconfirm>
          </Space>
        ) }:null].filter(Boolean)} />
      </div>
      {selectedBank && (
        <div style={{ marginTop: 24 }}>
          <h3>题库: {selectedBank.name} 题目列表</h3>
          <Table rowKey='id' loading={loadingQuestions} dataSource={questions} columns={questionColumns} size='small' pagination={{ pageSize: 20 }} />
        </div>
      )}
      <Modal open={bankModalOpen} title='新建题库' onCancel={()=>setBankModalOpen(false)} onOk={handleCreateBank} confirmLoading={creating} destroyOnClose okButtonProps={{ disabled: !canManage }}>
        <Form form={form} layout='vertical'>
          <Form.Item name='name' label='名称' rules={[{ required: true, message: '请输入名称' }]} validateTrigger={['onChange','onBlur']}>
            <Input maxLength={128} onChange={()=>{ form.validateFields(['name']); }} />
          </Form.Item>
          <Form.Item name='bank_type' label='题库类型' initialValue='single' rules={[{ required: true, message: '请选择题库类型' }]}>
            <Select>
              <Select.Option value='single'>单选题</Select.Option>
              <Select.Option value='multiple'>多选题</Select.Option>
              <Select.Option value='truefalse'>判断题</Select.Option>
              <Select.Option value='short_answer'>简答题</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name='default_question_score' label='单题分值' initialValue={1}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name='description' label='描述'> <Input.TextArea rows={3} maxLength={255} /> </Form.Item>
        </Form>
        {!canManage && <div style={{ marginTop:8, fontSize:12, color:'#dc2626' }}>当前账号没有题库管理权限，仅查看。</div>}
      </Modal>
      <Modal open={uploadVisible} title={`Excel 上传题目`} onCancel={()=>{setUploadVisible(false); setUploadResult(null); setFileList([]);}} onOk={handleDoUpload} confirmLoading={uploading} okText='开始解析' destroyOnClose okButtonProps={{ disabled: !canManage || !selectedBank || !fileList.length }}>
        <p style={{ marginBottom: 8 }}>支持列: 题型 | 题干 | A | B | C | D | E | F | G | H | 正确答案 | 默认分数 | 答案解析 | 知识类别 | 重要等级 | 题库试题分类<br/>缺失列按空处理, 题型默认为 single。</p>
        <Select
          placeholder='选择目标题库'
          style={{ width:'100%', marginBottom:12 }}
          value={selectedBank?.id}
          onChange={(id)=>{ const bank = banks.find(b=>b.id===id); setSelectedBank(bank || null); }}
        >
          {banks.map(b=> <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
        </Select>
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>选择 Excel 文件</Button>
        </Upload>
        {uploadResult && <div style={{ marginTop: 12, fontSize: 13 }}>
          <b>结果:</b> 新增 {uploadResult.inserted} / 更新 {uploadResult.updated} / 跳过 {uploadResult.skipped} / 总计 {uploadResult.total}
        </div>}
        {!selectedBank && <div style={{ marginTop:8, color:'#dc2626', fontSize:12 }}>请先选择题库再开始解析。</div>}
        {!canManage && <div style={{ marginTop:4, color:'#64748b', fontSize:12 }}>当前账号没有上传权限，仅展示界面。</div>}
      </Modal>
      <Modal open={bankImportVisible} title='题库导入 (Excel)' onCancel={()=>{ setBankImportVisible(false); setBankImportResult(null); setBankImportFileList([]); }} onOk={handleDoBankImport} confirmLoading={bankImportUploading} okText='开始导入' destroyOnClose okButtonProps={{ disabled: !canManage || !bankImportFileList.length }}>
        <p style={{ marginBottom:8 }}>支持列: 名称 | 描述 (第一张工作表)。同名题库将更新描述, 否则新增。</p>
        <Upload {...bankImportUploadProps}>
          <Button icon={<UploadOutlined />}>选择 Excel 文件</Button>
        </Upload>
        {bankImportResult && <div style={{ marginTop: 12, fontSize: 13 }}>
          <b>结果:</b> 新增 {bankImportResult.inserted} / 更新 {bankImportResult.updated} / 跳过 {bankImportResult.skipped} / 总计 {bankImportResult.total}
        </div>}
      </Modal>
      <Modal open={groupModalOpen} title={editingGroup?.id ? '编辑题库组合' : '新建题库组合'} onCancel={()=>{ setGroupModalOpen(false); setEditingGroup(null); }} onOk={handleCreateGroup} okText='保存' destroyOnClose>
        <Form form={groupForm} layout='vertical'>
          <Form.Item name='name' label='名称' rules={[{ required: true, message: '请输入名称' }]} validateTrigger={['onChange','onBlur']}>
            <Input onChange={()=>{ groupForm.validateFields(['name']); }} />
          </Form.Item>
          <Form.Item name='description' label='描述'> <Input.TextArea rows={2} /> </Form.Item>
          <Form.Item name='bank_ids' label='包含题库' rules={[{ required: true, message: '请至少选择一个题库' }]}>
            <Select mode='multiple' placeholder='选择题库' maxTagCount='responsive'>
              {banks.map(b => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      <Modal open={examModalOpen} title={editingExam?.id ? '编辑考试' : '新建考试'} onCancel={()=>{ setExamModalOpen(false); setEditingExam(null); }} onOk={handleCreateExam} okText={editingExam?.id ? '保存' : '创建'} confirmLoading={creatingExam} destroyOnClose>
        <Form form={examForm} layout='vertical' initialValues={{ mode: 'fixed' }}>
          <Form.Item name='title' label='标题' rules={[{ required: true, message: '请输入标题' }]} validateTrigger={['onChange','onBlur']}>
            <Input onChange={()=>{ examForm.validateFields(['title']); }} />
          </Form.Item>
          <Form.Item name='description' label='描述'> <Input.TextArea rows={2} /> </Form.Item>
          <Form.Item name='bank_ids' label='题库（可多选）'>
            <Select mode='multiple' allowClear placeholder='选择题库（推荐）' maxTagCount='responsive'>
              {banks.map(b => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name='bank_group_id' label='题库组合'>
            <Select allowClear placeholder='选择题库组合（可选）'>
              {groups.map(g => <Select.Option key={g.id} value={g.id}>{g.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name='mode' label='模式'>
            <Select>
              <Select.Option value='fixed'>固定试卷</Select.Option>
              <Select.Option value='random'>随机抽题</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label='题型数量（随机模式生效）'>
            <Space wrap>
              <Form.Item name='single_count' label='单选' style={{ marginBottom: 0 }}>
                <InputNumber min={0} precision={0} style={{ width: 100 }} />
              </Form.Item>
              <Form.Item name='multiple_count' label='多选' style={{ marginBottom: 0 }}>
                <InputNumber min={0} precision={0} style={{ width: 100 }} />
              </Form.Item>
              <Form.Item name='truefalse_count' label='判断' style={{ marginBottom: 0 }}>
                <InputNumber min={0} precision={0} style={{ width: 100 }} />
              </Form.Item>
              <Form.Item name='short_answer_count' label='简答' style={{ marginBottom: 0 }}>
                <InputNumber min={0} precision={0} style={{ width: 100 }} />
              </Form.Item>
            </Space>
          </Form.Item>
          <Form.Item name='duration_minutes' label='考试时长(分钟)'> <InputNumber min={1} style={{ width: '100%' }} /> </Form.Item>
          <Form.Item name='pass_score' label='及格分'> <InputNumber min={0} step={1} style={{ width: '100%' }} /> </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  const BankQuestionSearchView = (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select
          mode='multiple'
          style={{ width: isMobile ? '100%' : 380 }}
          placeholder='选择题库（可选）'
          value={questionSearchBankIds}
          onChange={setQuestionSearchBankIds}
          maxTagCount='responsive'
        >
          {banks.map((b) => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
        </Select>
        <Input
          style={{ width: isMobile ? '100%' : 360 }}
          placeholder='输入题干/选项/答案关键词'
          value={questionSearchKeyword}
          onChange={(e) => setQuestionSearchKeyword(e.target.value)}
          onPressEnter={executeQuestionSearch}
          allowClear
        />
        <Button type='primary' loading={questionSearchLoading} onClick={executeQuestionSearch}>搜索题目</Button>
        <Button onClick={resetQuestionSearch}>重置</Button>
      </div>

      <div style={{ marginBottom: 10, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(auto-fit,minmax(160px,1fr))', gap: 8 }}>
        <div>结果数：{questionSearchResults.length}</div>
        <div>当前：{questionSearchResults.length ? `${questionSearchIndex + 1}/${questionSearchResults.length}` : '-'}</div>
      </div>

      {questionSearchResults.length === 0 && !questionSearchLoading && <div style={{ color: '#64748b' }}>请输入关键词或选择题库后搜索。</div>}

      {questionSearchResults.length > 0 && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
          {renderQuestionSearchCard(questionSearchCurrent, questionSearchIndex)}

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={goToPrevQuestionSearch} disabled={questionSearchIndex === 0}>上一题</Button>
            <Button onClick={goToNextQuestionSearch} disabled={questionSearchIndex >= questionSearchResults.length - 1}>下一题</Button>
          </Space>

          <div style={{ marginTop: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>答题卡</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6,minmax(0,1fr))' : 'repeat(10,minmax(0,1fr))', gap: 8 }}>
              {questionSearchResults.map((q, idx) => {
                const isCurrent = idx === questionSearchIndex;
                return (
                  <Button
                    key={q.id}
                    size='small'
                    onClick={() => goToQuestionSearchIndex(idx)}
                    style={isCurrent ? { borderColor: '#1d4ed8', boxShadow: '0 0 0 1px #1d4ed8 inset', color: '#1d4ed8' } : undefined}
                  >
                    {idx + 1}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const BanksView = (
    <Tabs
      activeKey={banksInnerTab}
      onChange={setBanksInnerTab}
      items={[
        { key: 'manage', label: '题库管理', children: BanksManageView },
        { key: 'search', label: '题目搜索', children: BankQuestionSearchView }
      ]}
    />
  );

  const PracticeSelectView = (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Select
          mode='multiple'
          style={{ width: isMobile ? '100%' : 420 }}
          placeholder='选择题库'
          value={practiceBanks}
          onChange={setPracticeBanks}
          maxTagCount='responsive'
        >
          {banks.map((b) => <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>)}
        </Select>
        <Button onClick={executePractice} type='primary' loading={practiceLoading}>开始练习</Button>
        {practiceQuestions.length > 0 && !practiceShowResult && <Button onClick={finishPractice} loading={practiceSubmitting}>结束练习</Button>}
        {practiceQuestions.length > 0 && practiceShowResult && <Button onClick={resetPracticeRun}>再练一次</Button>}
      </div>

      {practiceSummary && practiceShowResult && (
        <div style={{ marginBottom: 10, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(auto-fit,minmax(150px,1fr))', gap: 8 }}>
          <div>题目数：{practiceSummary?.question_count ?? 0}</div>
          <div>已答：{practiceSummary?.answered_count ?? 0}</div>
          <div>答对：{practiceSummary?.correct_count ?? 0}</div>
          <div>正确率：{practiceSummary?.score_rate ?? 0}%</div>
        </div>
      )}

      {practiceQuestions.length === 0 && <div style={{ color: '#64748b' }}>请选择题库并开始练习。</div>}

      {practiceQuestions.length > 0 && (
        <div
          onTouchStart={handlePracticeTouchStart}
          onTouchEnd={handlePracticeTouchEnd}
          onMouseDown={handlePracticeMouseDown}
          onMouseUp={handlePracticeMouseUp}
          style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc', touchAction: 'pan-y' }}
        >
          <div style={{ marginBottom: 8, fontSize: 13, color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>第 {practiceCurrentQuestionIndex + 1} / {practiceQuestions.length} 题</span>
            <span>已答 {practiceAnsweredCount} 题</span>
          </div>
          {isMobile && <div style={{ marginBottom: 8, fontSize: 12, color: '#64748b' }}>左右滑动切换题目</div>}

          {renderPracticeQuestion(practiceCurrentQuestion, practiceCurrentQuestionIndex)}

          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={goToPrevPracticeQuestion} disabled={practiceCurrentQuestionIndex === 0}>上一题</Button>
            <Button onClick={goToNextPracticeQuestion} disabled={practiceCurrentQuestionIndex >= practiceQuestions.length - 1}>下一题</Button>
          </Space>

          <div style={{ marginTop: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>答题卡</div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6,minmax(0,1fr))' : 'repeat(10,minmax(0,1fr))', gap: 8 }}>
              {practiceQuestions.map((q, idx) => {
                const answered = !!practiceAnswers[q.id];
                const isCurrent = idx === practiceCurrentQuestionIndex;
                return (
                  <Button
                    key={q.id}
                    size='small'
                    type={answered ? 'primary' : 'default'}
                    ghost={answered && !isCurrent}
                    onClick={() => goToPracticeQuestion(idx)}
                    style={isCurrent ? { borderColor: '#1d4ed8', boxShadow: '0 0 0 1px #1d4ed8 inset' } : undefined}
                  >
                    {idx + 1}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const PracticeRecordsView = (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>题库练习记录</div>
        <Button onClick={fetchPracticeRecords} loading={practiceRecordsLoading}>刷新</Button>
      </div>

      <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(auto-fit,minmax(150px,1fr))', gap: 8 }}>
        <div>总场次：{practiceRecordsSummary?.total_count ?? 0}</div>
        <div>已提交：{practiceRecordsSummary?.submitted_count ?? 0}</div>
        <div>平均正确率：{practiceRecordsSummary?.avg_score_rate ?? 0}%</div>
      </div>

      {isMobile ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {practiceRecords.map((r) => (
            <div key={r.session_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#fff' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>练习#{r.session_id}</div>
              <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                <div>开始：{r.started_at || '-'}</div>
                <div>提交：{r.submitted_at || '-'}</div>
                <div>题目：{r.question_count ?? 0}</div>
                <div>已答：{r.answered_count ?? 0}</div>
                <div>答对：{r.correct_count ?? 0}</div>
                <div>正确率：{r.score_rate ?? 0}%</div>
                <div>状态：{r.status || '-'}</div>
              </div>
              <Space style={{ marginTop: 8 }}>
                <Button size='small' onClick={() => openPracticeRecordCard(r)}>答题卡</Button>
                <Popconfirm title='确定删除该练习记录？' onConfirm={() => handleDeletePracticeRecord(r)}>
                  <Button size='small' danger loading={deletePracticeSessionId === r.session_id}>删除</Button>
                </Popconfirm>
              </Space>
            </div>
          ))}
          {!practiceRecords.length && !practiceRecordsLoading && <div style={{ color: '#64748b' }}>暂无练习记录</div>}
        </div>
      ) : (
        <Table
          rowKey='session_id'
          loading={practiceRecordsLoading}
          dataSource={practiceRecords}
          size='small'
          pagination={{ pageSize: 8 }}
          scroll={{ x: 980 }}
          columns={[
            { title: '练习ID', dataIndex: 'session_id', width: 90 },
            { title: '开始时间', dataIndex: 'started_at', width: 170 },
            { title: '提交时间', dataIndex: 'submitted_at', width: 170, render: (v) => v || '-' },
            { title: '题目数', dataIndex: 'question_count', width: 90, render: (v) => v ?? 0 },
            { title: '已答', dataIndex: 'answered_count', width: 80, render: (v) => v ?? 0 },
            { title: '答对', dataIndex: 'correct_count', width: 80, render: (v) => v ?? 0 },
            { title: '正确率', dataIndex: 'score_rate', width: 90, render: (v) => `${v ?? 0}%` },
            { title: '时长', dataIndex: 'duration_seconds', width: 110, render: (v) => formatDuration(v) },
            { title: '状态', dataIndex: 'status', width: 90 },
            { title: '答题卡', width: 100, render: (_, r) => <Button size='small' onClick={() => openPracticeRecordCard(r)}>查看</Button> }
            , { title: '操作', width: 90, render: (_, r) => (
              <Popconfirm title='确定删除该练习记录？' onConfirm={() => handleDeletePracticeRecord(r)}>
                <Button size='small' danger loading={deletePracticeSessionId === r.session_id}>删除</Button>
              </Popconfirm>
            ) }
          ]}
        />
      )}
    </div>
  );

  const PracticeView = (
    <Tabs
      activeKey={practiceInnerTab}
      onChange={setPracticeInnerTab}
      items={[
        { key: 'select', label: '选择题库', children: PracticeSelectView },
        { key: 'records', label: '练习记录', children: PracticeRecordsView }
      ]}
    />
  );

  const ExamsCenter = (
    <div>
      {!activeExam && (
        <>
          <p>选择已发布考试并开始作答。</p>
          <Table rowKey='id' dataSource={exams.filter(e=>e.status==='published')} size='small' loading={loadingExams} pagination={{ pageSize: 5 }} columns={[{ title:'ID', dataIndex:'id', width:70 },{ title:'标题', dataIndex:'title' },{ title:'模式', dataIndex:'mode', width:90 },{ title:'发布类型', dataIndex:'publish_type', width:100, render:(v)=> String(v || '').toLowerCase()==='mock' ? '模拟考' : '正式考试' },{ title:'时长(分)', dataIndex:'duration_minutes', width:90 },{ title:'及格分', dataIndex:'pass_score', width:80 }, canStats ? { title:'统计', width:100, render:(_,r)=> <Button size='small' onClick={()=>openExamStats(r)}>数据统计</Button> } : null, canStats ? { title:'错题再练', width:110, render:(_,r)=> <Button size='small' onClick={()=>openWrongPractice(r)}>集中练习</Button> } : null, { title:'操作', width:120, render:(_,r)=> <Button type='primary' size='small' onClick={()=>beginExam(r)} disabled={examLoading}>开始考试</Button>}].filter(Boolean)} />
        </>
      )}
      {activeExam && (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:16, background:'#fff', marginTop:12 }}>
          <Space style={{ display:'flex', justifyContent:'space-between', width:'100%', marginBottom:12 }}>
            <span style={{ fontWeight:600, fontSize: isMobile ? 16 : 22 }}>{activeExam.title}</span>
            <Space>
              {renderTimer()}
              <Button size='small' danger onClick={handleExitExam} disabled={examSubmitting}>退出</Button>
              {!examResult && <Button size='small' type='primary' loading={examSubmitting} onClick={submitExam}>提交</Button>}
            </Space>
          </Space>
          {examLoading && <div>加载题目中...</div>}
          {!examLoading && examQuestions.length > 0 && (
            <div
              onTouchStart={handleQuestionTouchStart}
              onTouchEnd={handleQuestionTouchEnd}
              onMouseDown={handleQuestionMouseDown}
              onMouseUp={handleQuestionMouseUp}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc', touchAction: 'pan-y' }}
            >
              <div style={{ marginBottom: 8, fontSize: 13, color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>第 {currentQuestionIndex + 1} / {examQuestions.length} 题</span>
                <span>已答 {answeredExamCount} 题</span>
              </div>
              {isMobile && <div style={{ marginBottom: 8, fontSize: 12, color: '#64748b' }}>左右滑动切换题目</div>}
              {renderExamQuestion(examQuestions[currentQuestionIndex], currentQuestionIndex)}
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Button onClick={goToPrevQuestion} disabled={currentQuestionIndex === 0}>上一题</Button>
                <Button onClick={goToNextQuestion} disabled={currentQuestionIndex >= examQuestions.length - 1}>下一题</Button>
              </Space>
              <div style={{ marginTop: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>答题卡</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6,minmax(0,1fr))' : 'repeat(10,minmax(0,1fr))', gap: 8 }}>
                  {examQuestions.map((q, idx) => {
                    const answered = !!answers[q.id];
                    const isCurrent = idx === currentQuestionIndex;
                    return (
                      <Button
                        key={q.id}
                        size='small'
                        type={answered ? 'primary' : 'default'}
                        ghost={answered && !isCurrent}
                        onClick={() => goToQuestion(idx)}
                        style={isCurrent ? { borderColor: '#1d4ed8', boxShadow: '0 0 0 1px #1d4ed8 inset' } : undefined}
                      >
                        {idx + 1}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {!examLoading && examQuestions.length === 0 && <div style={{ color: '#64748b' }}>暂无题目，请返回后重试。</div>}
          {examResult && <div style={{ fontWeight:600, color: examResult.passed? '#16a34a':'#dc2626' }}>总分 {examResult.total_score} {examResult.passed? '通过':'未通过'}</div>}
        </div>
      )}
    </div>
  );

  const MyExamsView = (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>当前用户参加考试记录</div>
        <Button onClick={fetchMyExams} loading={myExamsLoading}>刷新</Button>
      </div>
      <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
        <div>总场次：{myExamsSummary?.total_count ?? 0}</div>
        <div>已交卷：{myExamsSummary?.submitted_count ?? 0}</div>
        <div>通过：{myExamsSummary?.passed_count ?? 0}</div>
        <div>通过率：{myExamsSummary?.pass_rate ?? 0}%</div>
        <div>平均分：{myExamsSummary?.avg_score ?? 0}</div>
        <div>平均时长：{formatDuration(myExamsSummary?.avg_duration_seconds || 0)}</div>
      </div>
      {isMobile ? (
        <div style={{ display: 'grid', gap: 10 }}>
          {myExamsAttempts.map((r) => (
            <div key={r.attempt_id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, background: '#fff' }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>{r.exam_title || `考试#${r.exam_id || '-'}`}</div>
              <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.8 }}>
                <div>作答ID：{r.attempt_id}</div>
                <div>类型：{String(r.publish_type || '').toLowerCase() === 'mock' ? '模拟考' : '正式考试'}</div>
                <div>开始：{r.started_at || '-'}</div>
                <div>交卷：{r.submitted_at || '-'}</div>
                <div>分数：{r.total_score ?? '-'}</div>
                <div>时长：{formatDuration(r.duration_seconds)}</div>
                <div>状态：{r.status || '-'}</div>
              </div>
              <Space style={{ marginTop: 8 }}>
                {String(r.status || '').toLowerCase() === 'in_progress' && String(r.publish_type || '').toLowerCase() === 'mock' && (
                  <Button size='small' type='primary' onClick={() => continueMockExam(r)}>继续考试</Button>
                )}
                <Button size='small' onClick={() => openAnswerCard({ id: r.attempt_id })}>答题卡</Button>
                <Popconfirm title='确定删除该考试记录？' onConfirm={() => handleDeleteMyAttempt(r)}>
                  <Button size='small' danger loading={deleteAttemptId === r.attempt_id}>删除</Button>
                </Popconfirm>
              </Space>
            </div>
          ))}
          {!myExamsAttempts.length && !myExamsLoading && <div style={{ color: '#64748b' }}>暂无考试记录</div>}
        </div>
      ) : (
        <Table
          rowKey='attempt_id'
          loading={myExamsLoading}
          dataSource={myExamsAttempts}
          size='small'
          pagination={{ pageSize: 8 }}
          scroll={{ x: 980 }}
          columns={[
            { title: '作答ID', dataIndex: 'attempt_id', width: 90 },
            { title: '考试', dataIndex: 'exam_title' },
            { title: '类型', dataIndex: 'publish_type', width: 90, render: (v) => String(v || '').toLowerCase() === 'mock' ? '模拟考' : '正式考试' },
            { title: '开始时间', dataIndex: 'started_at', width: 170 },
            { title: '交卷时间', dataIndex: 'submitted_at', width: 170, render: (v) => v || '-' },
            { title: '分数', dataIndex: 'total_score', width: 90, render: (v) => v ?? '-' },
            { title: '时长', dataIndex: 'duration_seconds', width: 120, render: (v) => formatDuration(v) },
            { title: '状态', dataIndex: 'status', width: 90 },
            { title: '答题卡', width: 100, render: (_, r) => <Button size='small' onClick={() => openAnswerCard({ id: r.attempt_id })}>查看</Button> },
            {
              title: '操作',
              width: 180,
              render: (_, r) => (
                <Space>
                  {String(r.status || '').toLowerCase() === 'in_progress' && String(r.publish_type || '').toLowerCase() === 'mock' && (
                    <Button size='small' type='primary' onClick={() => continueMockExam(r)}>继续</Button>
                  )}
                  <Popconfirm title='确定删除该考试记录？' onConfirm={() => handleDeleteMyAttempt(r)}>
                    <Button size='small' danger loading={deleteAttemptId === r.attempt_id}>删除</Button>
                  </Popconfirm>
                </Space>
              )
            }
          ]}
        />
      )}
    </div>
  );

  return (
    <div style={{ padding: 16 }}>
      <h2>考试系统</h2>
      <Tabs
        activeKey={activeTab}
        onChange={handleMainTabChange}
        items={[
          (canView || canManage) ? { key: 'banks', label: '题库管理', children: BanksView } : null,
          canAttempt ? { key: 'practice', label: '题库练习', children: PracticeView } : null,
          (canAttempt || canView) ? { key: 'exams', label: '考试中心', children: ExamsCenter } : null,
          canMyExams ? { key: 'my-exams', label: '我的考试', children: MyExamsView } : null
        ].filter(Boolean)}
      />

      <Modal
        open={publishModalOpen}
        title='发布考试'
        onCancel={() => { setPublishModalOpen(false); setPublishTargetExam(null); }}
        onOk={handlePublishExam}
        confirmLoading={publishingId === publishTargetExam?.id}
        okText='确认发布'
        destroyOnClose
      >
        <div style={{ marginBottom: 10, color: '#475569', fontSize: 13 }}>
          {publishTargetExam?.title ? `考试：${publishTargetExam.title}` : '请选择发布类型'}
        </div>
        <Select value={publishType} onChange={setPublishType} style={{ width: '100%' }}>
          <Select.Option value='formal'>正式考试</Select.Option>
          <Select.Option value='mock'>模拟考</Select.Option>
        </Select>
      </Modal>

      <Modal
        open={statsModalOpen}
        title={`考试统计${statsExam?.title ? ` - ${statsExam.title}` : ''}`}
        onCancel={() => { setStatsModalOpen(false); setStatsExam(null); setStatsSummary(null); setStatsAttempts([]); }}
        footer={null}
        width={980}
      >
        {statsLoading ? <div>加载统计中...</div> : (
          <>
            <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10 }}>
              <div>参考人数：{statsSummary?.participant_count ?? 0}</div>
              <div>已交卷：{statsSummary?.submitted_count ?? 0}</div>
              <div>通过人数：{statsSummary?.passed_count ?? 0}</div>
              <div>通过率：{statsSummary?.pass_rate ?? 0}%</div>
              <div>平均分：{statsSummary?.avg_score ?? 0}</div>
              <div>平均时长：{formatDuration(statsSummary?.avg_duration_seconds || 0)}</div>
            </div>
            <Table
              rowKey='id'
              dataSource={statsAttempts}
              size='small'
              pagination={{ pageSize: 8 }}
              columns={[
                { title: '作答ID', dataIndex: 'id', width: 90 },
                { title: '参考人员', dataIndex: 'user_name', width: 140, render: (v) => v || '-' },
                { title: '开始时间', dataIndex: 'started_at', width: 120, render: (v) => formatTimeOnly(v) },
                { title: '交卷时间', dataIndex: 'submitted_at', width: 120, render: (v) => formatTimeOnly(v) },
                { title: '分数', dataIndex: 'total_score', width: 90, render: (v) => v ?? '-' },
                { title: '时长', dataIndex: 'duration_seconds', width: 120, render: (v) => formatDuration(v) },
                { title: '状态', dataIndex: 'status', width: 90 },
                { title: '答题卡', width: 100, render: (_, r) => <Button size='small' onClick={() => openAnswerCard(r)}>查看</Button> }
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        open={wrongPracticeModalOpen}
        title={`错题集中再练${wrongPracticeExam?.title ? ` - ${wrongPracticeExam.title}` : ''}`}
        onCancel={() => {
          setWrongPracticeModalOpen(false);
          setWrongPracticeExam(null);
          setWrongPracticeSummary(null);
          setWrongPracticeQuestions([]);
          setWrongPracticeAnswers({});
          setWrongPracticeShowResult(false);
          setWrongPracticeIndex(0);
        }}
        footer={null}
        width={980}
      >
        {wrongPracticeLoading ? <div>加载错题中...</div> : (
          <>
            <div style={{ marginBottom: 10, display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(auto-fit,minmax(160px,1fr))', gap: 8 }}>
              <div>错题数：{wrongPracticeSummary?.question_count ?? wrongPracticeQuestions.length}</div>
              <div>累计错答：{wrongPracticeSummary?.total_wrong_answers ?? 0}</div>
              <div>已答：{wrongPracticeAnsweredCount}</div>
              <div>状态：{wrongPracticeShowResult ? '已查看答案' : '练习中'}</div>
            </div>

            {wrongPracticeQuestions.length > 0 ? (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                <div style={{ marginBottom: 8, fontSize: 13, color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>第 {wrongPracticeIndex + 1} / {wrongPracticeQuestions.length} 题</span>
                  <Space>
                    {!wrongPracticeShowResult ? <Button size='small' type='primary' onClick={() => setWrongPracticeShowResult(true)}>结束练习并查看答案</Button> : <Button size='small' onClick={() => { setWrongPracticeShowResult(false); setWrongPracticeAnswers({}); setWrongPracticeIndex(0); }}>再练一次</Button>}
                  </Space>
                </div>

                {renderWrongPracticeQuestion(wrongPracticeCurrentQuestion, wrongPracticeIndex)}

                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button onClick={goToPrevWrongPractice} disabled={wrongPracticeIndex === 0}>上一题</Button>
                  <Button onClick={goToNextWrongPractice} disabled={wrongPracticeIndex >= wrongPracticeQuestions.length - 1}>下一题</Button>
                </Space>

                <div style={{ marginTop: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>错题答题卡（红色为本次练习错题）</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6,minmax(0,1fr))' : 'repeat(10,minmax(0,1fr))', gap: 8 }}>
                    {wrongPracticeQuestions.map((q, idx) => {
                      const isCurrent = idx === wrongPracticeIndex;
                      const answered = !!wrongPracticeAnswers[q.id];
                      const judge = getWrongPracticeJudge(q);
                      const isWrong = wrongPracticeShowResult && judge.isWrong;
                      const isCorrect = wrongPracticeShowResult && judge.isCorrect;

                      let style;
                      if (isWrong) style = { borderColor: '#ef4444', color: '#dc2626', background: '#fef2f2' };
                      if (isCorrect) style = { borderColor: '#16a34a', color: '#15803d', background: '#f0fdf4' };
                      if (isCurrent) {
                        style = { ...(style || {}), boxShadow: '0 0 0 1px #1d4ed8 inset', borderColor: '#1d4ed8' };
                      }

                      return (
                        <Button
                          key={q.id}
                          size='small'
                          type={answered ? 'primary' : 'default'}
                          ghost={answered && !isCurrent}
                          onClick={() => goToWrongPracticeQuestion(idx)}
                          style={style}
                        >
                          {idx + 1}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#64748b' }}>该场考试暂无错题可集中练习。</div>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={practiceRecordModalOpen}
        title={`练习答题卡${practiceRecordSession?.session_id ? ` - #${practiceRecordSession.session_id}` : ''}`}
        onCancel={() => {
          setPracticeRecordModalOpen(false);
          setPracticeRecordSession(null);
          setPracticeRecordRows([]);
          setPracticeRecordIndex(0);
        }}
        footer={null}
        width={980}
      >
        {practiceRecordLoading ? <div>加载练习答题卡中...</div> : (
          <>
            {practiceRecordRows.length > 0 ? (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                <div style={{ marginBottom: 8, fontSize: 13, color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>第 {practiceRecordIndex + 1} / {practiceRecordRows.length} 题</span>
                  <span>答错 {practiceRecordWrongCount} 题</span>
                </div>

                {renderPracticeRecordReviewQuestion(practiceRecordCurrentRow, practiceRecordIndex)}

                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button onClick={goToPrevPracticeRecord} disabled={practiceRecordIndex === 0}>上一题</Button>
                  <Button onClick={goToNextPracticeRecord} disabled={practiceRecordIndex >= practiceRecordRows.length - 1}>下一题</Button>
                </Space>

                <div style={{ marginTop: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>答题卡（红色为错题）</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6,minmax(0,1fr))' : 'repeat(10,minmax(0,1fr))', gap: 8 }}>
                    {practiceRecordRows.map((row, idx) => {
                      const isCurrent = idx === practiceRecordIndex;
                      const isWrong = Number(row?.is_correct) === 0;
                      const isCorrect = Number(row?.is_correct) === 1;
                      let style;
                      if (isWrong) style = { borderColor: '#ef4444', color: '#dc2626', background: '#fef2f2' };
                      if (isCorrect) style = { borderColor: '#16a34a', color: '#15803d', background: '#f0fdf4' };
                      if (isCurrent) style = { ...(style || {}), boxShadow: '0 0 0 1px #1d4ed8 inset', borderColor: '#1d4ed8' };
                      return (
                        <Button key={row.id || row.question_id || idx} size='small' onClick={() => goToPracticeRecordQuestion(idx)} style={style}>
                          {idx + 1}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#64748b' }}>暂无练习答题卡数据</div>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={answerModalOpen}
        title={`答题卡${(answerAttempt?.id || answerAttempt?.attempt_id) ? ` - #${answerAttempt?.id || answerAttempt?.attempt_id}` : ''}`}
        onCancel={() => { setAnswerModalOpen(false); setAnswerAttempt(null); setAnswerRows([]); setAnswerQuestionIndex(0); }}
        footer={null}
        width={980}
      >
        {answerLoading ? <div>加载答题卡中...</div> : (
          <>
            {answerRows.length > 0 ? (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
                <div style={{ marginBottom: 8, fontSize: 13, color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>第 {answerQuestionIndex + 1} / {answerRows.length} 题</span>
                  <span>答错 {answerWrongCount} 题</span>
                </div>
                {renderAnswerReviewQuestion(answerCurrentRow, answerQuestionIndex)}
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Button onClick={goToPrevAnswerQuestion} disabled={answerQuestionIndex === 0}>上一题</Button>
                  <Button onClick={goToNextAnswerQuestion} disabled={answerQuestionIndex >= answerRows.length - 1}>下一题</Button>
                </Space>
                <div style={{ marginTop: 12, borderTop: '1px dashed #cbd5e1', paddingTop: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>答题卡（红色为错题）</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(6,minmax(0,1fr))' : 'repeat(10,minmax(0,1fr))', gap: 8 }}>
                    {answerRows.map((row, idx) => {
                      const isCurrent = idx === answerQuestionIndex;
                      const isWrong = Number(row?.is_correct) === 0;
                      const isCorrect = Number(row?.is_correct) === 1;
                      let style;
                      if (isWrong) style = { borderColor: '#ef4444', color: '#dc2626', background: '#fef2f2' };
                      if (isCorrect) style = { borderColor: '#16a34a', color: '#15803d', background: '#f0fdf4' };
                      if (isCurrent) {
                        style = { ...(style || {}), boxShadow: '0 0 0 1px #1d4ed8 inset', borderColor: '#1d4ed8' };
                      }
                      return (
                        <Button key={row.id || row.question_id || idx} size='small' onClick={() => goToAnswerQuestion(idx)} style={style}>
                          {idx + 1}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#64748b' }}>暂无答题卡数据</div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ExamSystemPage;
